import { useState } from "react";
import MembershipAgreementForm from "./MembershipAgreementForm";

type SanctuaryEntryScreenProps = {
  onEnter: () => void;
};

const FREE_WORKSHOP_LINK = "https://calm-but-capable.kit.com/3e9b23e333";

const examples = [
  "Start a side venture or business.",
  "Turn an idea into a product or service.",
  "Write and publish a book.",
  "Create a website or develop an app idea.",
  "Learn a new skill.",
  "Prepare for a career change.",
  "Plan a personal or professional project.",
  "Solve an immediate practical problem.",
  "Restart something you previously abandoned.",
  "Work towards a dream that has always felt too large.",
];

const membershipBenefits = [
  "Your own personal Sanctuary workspace.",
  "Support to clarify what you want to achieve.",
  "Help understanding what may be stopping you.",
  "Practical plans broken into manageable steps.",
  "Step-by-step instructions whenever you need them.",
  "A place to record ideas, actions and progress.",
  "Reflection that helps turn experience into evidence.",
  "Continuing context, so you can return without starting again.",
  "Access to the Calm But Capable workshop.",
];

export default function SanctuaryEntryScreen({
  onEnter,
}: SanctuaryEntryScreenProps) {
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "annual" | null>(null);

  return (
    <main className="entry-container sanctuary-sales-page">
      <header className="entry-header">
        <div className="entry-brand">
          <img
            src="/logo-white.png"
            alt="Calm But Capable"
            className="entry-logo-small"
          />
          <div>
            <div className="entry-brand-name">Calm But Capable</div>
            <div className="entry-brand-subtitle">The Sanctuary</div>
          </div>
        </div>

        <button className="entry-login-button" onClick={onEnter}>
          Login
        </button>
      </header>

      <section className="entry-card sanctuary-hero">
        <div className="entry-logo-wrap">
          <img
            src="/logo-white.png"
            alt=""
            aria-hidden="true"
            className="entry-logo"
          />
        </div>

        <p className="entry-eyebrow">Calm But Capable presents</p>
        <h1 className="entry-title">The Sanctuary</h1>
        <h2 className="sanctuary-hero-question">
          Is there something you want to succeed at?
        </h2>

        <p className="entry-subtitle sanctuary-hero-copy">
          Perhaps you have a dream, task or project that matters to you—but you
          do not know where to begin.
        </p>

        <div className="sanctuary-recognition" aria-label="You may recognise this">
          <p>You may have an idea but cannot turn it into a practical plan.</p>
          <p>
            You may know what you want to change, but something keeps getting in
            your way.
          </p>
          <p>
            You may have tried before, become overwhelmed or reached a step you
            did not know how to complete.
          </p>
        </div>

        <p className="sanctuary-hero-belief">
          You know you are capable of more. You simply need a clear way forward
          and the right help when you become stuck.
        </p>

        <div className="sanctuary-answer">
          <p className="entry-eyebrow">The answer is here—with Calm But Capable</p>
          <p className="sanctuary-promise">
            You can succeed. Your dreams can come true.
          </p>
          <p>
            The Sanctuary helps you understand what is stopping you, turn what
            you want to achieve into manageable steps and learn how to complete
            each one.
          </p>
          <p>
            You still take the action—but you no longer have to work everything
            out alone.
          </p>
        </div>

        <a className="entry-button entry-button-link sanctuary-primary-cta" href="#membership-options">
          Take my next step
        </a>
      </section>

      <div className="entry-content sanctuary-sales-content">
        <section className="entry-section" id="how-it-works">
          <p className="entry-eyebrow membership-eyebrow">Practical help when you need it</p>
          <h2 className="entry-section-title">Bring your dream, task or project</h2>

          <div className="sanctuary-copy-block">
            <p>Tell The Sanctuary what you want to achieve.</p>
            <p>
              It will help you clarify the outcome, understand what may be
              getting in your way and break the journey down into clear,
              manageable steps.
            </p>
            <p>
              For each step, The Sanctuary can provide the information,
              practical instructions and tools you need to move forward with
              any problem, project or dream.
            </p>
          </div>

          <div className="entry-feature-list sanctuary-help-list">
            <div className="entry-feature">
              If you still do not understand, ask for the step to be explained
              more simply.
            </div>
            <div className="entry-feature">
              If it still feels too large, ask for it to be broken down further.
            </div>
            <div className="entry-feature">
              If you do not know how to do something, ask for practical,
              step-by-step instructions showing you what to do.
            </div>
            <div className="entry-feature">
              If a new problem appears, bring that problem back and ask for help.
            </div>
          </div>

          <div className="sanctuary-copy-block">
            <p>
              Your plan is not fixed. It can develop as you learn, make progress
              and discover what you need next.
            </p>
            <p className="sanctuary-section-closing">
              You bring the dream, task or project. The Sanctuary helps you
              create the route and teaches you how to travel it—one clear step
              at a time.
            </p>
          </div>
        </section>

        <section className="entry-section sanctuary-panel">
          <h2 className="entry-section-title">
            How can one system help with so many different things?
          </h2>
          <p className="membership-intro">
            Because although every goal is different, the process of moving
            forward follows the same foundations.
          </p>

          <div className="entry-steps">
            {[
              "Understand what you want to achieve.",
              "Discover what is stopping you.",
              "Find the next practical step.",
              "Learn how to complete it.",
              "Take action, review what happened and decide what comes next.",
            ].map((step, index) => (
              <div className="entry-step" key={step}>
                <span className="entry-step-number">{index + 1}</span>
                {step}
              </div>
            ))}
          </div>

          <h3 className="sanctuary-subheading">What could you bring?</h3>
          <div className="sanctuary-example-grid">
            {examples.map((example) => (
              <div className="sanctuary-example" key={example}>
                {example}
              </div>
            ))}
          </div>

          <p className="sanctuary-section-closing sanctuary-centred-copy">
            The Sanctuary does not need every journey to be the same. It helps
            you understand your destination, work out where you are now and
            create the route between them.
          </p>
          <p className="sanctuary-emphasis">Whatever you bring, it begins with one clear next step.</p>
        </section>

        <section className="entry-section">
          <p className="entry-eyebrow membership-eyebrow">More than one useful tool</p>
          <h2 className="entry-section-title">It is not another single-purpose app</h2>

          <div className="sanctuary-not-list">
            <p><strong>You can journal in The Sanctuary</strong>—but it is not a journalling app.</p>
            <p><strong>You can use it to organise your time</strong>—but it is not a time-management app.</p>
            <p><strong>It uses AI to understand what you need and help you move forward</strong>—but it is not simply another AI app.</p>
            <p><strong>It can help you reduce overwhelm, reflect and support your mental wellbeing</strong>—but it is not a wellbeing app, therapy or medical treatment.</p>
          </div>

          <div className="sanctuary-definition">
            <h3>Calm But Capable is a personal system for progress</h3>
            <p>
              It brings understanding, learning, problem-solving, planning,
              reflection and practical action together in one place—built
              around you and what you want to achieve.
            </p>
            <p>Other products help you reflect, plan, organise or learn.</p>
            <p className="sanctuary-emphasis">
              The Sanctuary brings those capabilities together in a unique way
              and uses them to help you achieve something that matters to you.
            </p>
          </div>
        </section>

        <section className="entry-section sanctuary-panel">
          <h2 className="entry-section-title">Build confidence through evidence</h2>
          <div className="sanctuary-copy-block sanctuary-centred-copy">
            <p>You do not need to feel completely confident before you begin.</p>
            <p>
              Confidence grows when you take action and see evidence of what
              you can do.
            </p>
            <p>
              The Sanctuary helps you begin with a step that is clear and
              manageable. When you complete it, you gain experience. That
              experience gives you evidence. The evidence helps you understand
              what worked, what needs to change and what you are capable of
              doing next.
            </p>
          </div>

          <div className="sanctuary-evidence-cycle" aria-label="How confidence develops">
            <span>Action creates experience.</span>
            <span>Experience creates evidence.</span>
            <span>Evidence builds confidence.</span>
            <span>Confidence makes the next action easier.</span>
          </div>

          <div className="sanctuary-copy-block sanctuary-centred-copy">
            <p>
              Not every step will work perfectly—and it does not need to. A
              setback is not proof that you cannot succeed. It is information
              that can help you understand the problem, adjust the plan and
              take a better-informed next step.
            </p>
            <p>You do not have to make one enormous leap.</p>
            <p>You only need to begin moving forward—one clear step at a time.</p>
            <p className="sanctuary-emphasis">That is what we do at Calm But Capable.</p>
          </div>
        </section>

        <section className="entry-section membership-section" id="membership-options">
          <p className="entry-eyebrow membership-eyebrow">Your next step</p>
          <h2 className="entry-section-title">The solution is The Sanctuary</h2>
          <p className="membership-intro">
            You have something you want to achieve. The Sanctuary gives you a
            place to understand it, plan it, break it down and learn how to take
            each step.
          </p>

          <div className="sanctuary-benefits" aria-label="Your membership includes">
            <h3>Your membership includes</h3>
            <ul>
              {membershipBenefits.map((benefit) => (
                <li key={benefit}>{benefit}</li>
              ))}
            </ul>
          </div>

          <h3 className="sanctuary-payment-heading">How would you like to pay?</h3>

          <div className="membership-plans">
            <article className="membership-plan">
              <p className="membership-plan-label">Monthly membership</p>
              <h3><span>£30</span> per month</h3>
              <p>Begin your journey with monthly payments.</p>
              <button className="entry-button entry-button-link" type="button" onClick={() => setSelectedPlan("monthly")}>Take my next step—join monthly</button>
              <p className="membership-terms-link">
                Read the <a href="/terms" target="_blank" rel="noopener noreferrer">membership terms</a> and <a href="/fair-use" target="_blank" rel="noopener noreferrer">Fair Use Policy</a> before joining. Public membership is for people aged 18 and over.
              </p>
            </article>

            <article className="membership-plan membership-plan-featured">
              <p className="membership-plan-badge">Best value</p>
              <p className="membership-plan-label">Yearly membership</p>
              <h3><span>£299</span> per year</h3>
              <p>Save £61 compared with twelve monthly payments.</p>
              <p className="membership-founder-offer">
                The first 50 yearly members can use <strong>FIRST50</strong> for
                £50 off the first year: pay £249 today. Membership renews
                automatically at £299 per year unless cancelled. Any future
                price change will be explained in advance.
              </p>
              <button className="entry-button entry-button-link" type="button" onClick={() => setSelectedPlan("annual")}>Take my next step—join yearly</button>
              <p className="membership-terms-link">
                Read the <a href="/terms" target="_blank" rel="noopener noreferrer">membership terms</a> and <a href="/fair-use" target="_blank" rel="noopener noreferrer">Fair Use Policy</a> before joining. Public membership is for people aged 18 and over.
              </p>
            </article>
          </div>

          {selectedPlan && <MembershipAgreementForm key={selectedPlan} plan={selectedPlan} onCancel={() => setSelectedPlan(null)} />}

          <p className="entry-small-print membership-payment-note">
            Secure payment is handled by Stripe. After payment, we will guide
            you through creating your Sanctuary account. If you already have an
            account, you can simply log in.
          </p>

          <p className="sanctuary-final-cta-copy">
            You have already thought about what you want to achieve.
            <strong> Clicking the button is your next step.</strong>
          </p>
        </section>

        <section className="entry-section sanctuary-workshop-fallback">
          <h2 className="entry-section-title">Not ready to join yet?</h2>
          <p>
            Begin with our free workshop and discover how one clear step can
            help you start moving forward.
          </p>
          <a className="entry-button entry-button-link sanctuary-secondary-button" href={FREE_WORKSHOP_LINK}>
            Get the free workshop
          </a>
          <p className="entry-small-print">
            After signing up, please check your inbox, spam, junk or promotions
            folder. Some secure email providers may block confirmation emails.
          </p>
        </section>
      </div>
    </main>
  );
}
